import React, { useId } from 'react';
import { Select } from 'antd';
import { comparisonIneligibilityLabel, VERSION_COMPARISON_MULTI_ERROR } from '../../../utils/versionComparison';
import './VersionComparisonSelector.css';

const versionLabel = row => row ? `${row.title || 'Sans titre'} · ${row.version ?? '?'}` : 'Document consulté';

export default function VersionComparisonSelector({ comparison, externalSelector, disabled = false, multi = false }) {
    const prefix = useId();
    if (!comparison) return <p role="alert">Sélection des versions indisponible. Rouvrez l’analyse.</p>;
    const { mode, rows, referenceId, loading, importing, tracking, viewedVersionId, completed } = comparison;
    const examined = rows.find(row => row.document_version_id === viewedVersionId);
    const references = rows.filter(row => row.document_version_id !== viewedVersionId);
    const reference = references.find(row => row.document_version_id === referenceId);
    const locked = disabled || loading || importing || completed;
    const ready = references.some(row => row.eligible_for_comparison);

    return <div className="version-comparison" onClick={event => event.stopPropagation()}>
        <div className="version-comparison__modes" role="group" aria-label="Source de comparaison">
            <button type="button" aria-pressed={mode === 'document_version'} disabled={disabled || importing}
                onClick={() => comparison.setMode('document_version')}>Versions de ce document</button>
            <button type="button" aria-pressed={mode === 'file'} disabled={disabled || importing}
                onClick={() => comparison.setMode('file')}>Autre document</button>
        </div>
        {mode === 'file' ? externalSelector : <>
            <div className="version-comparison__current">
                <small>Document à analyser</small>
                <strong>{versionLabel(examined)}</strong>
            </div>
            {completed ? <p role="status" className="version-comparison__direction">
                Une comparaison existe déjà pour cette version et ce point de vue.
                Consultez sa paire et ses preuves dans les commentaires. Les résultats ne seront pas remplacés.
            </p> : <>
                {multi && <p role="alert">{VERSION_COMPARISON_MULTI_ERROR}</p>}
                {loading && <p role="status">Chargement des versions…</p>}
                {comparison.error && <p role="alert">{comparison.error}</p>}
                {!loading && !comparison.error && !ready && <p className="version-comparison__empty">Aucune autre version prête à comparer</p>}
                {references.length > 0 && <label htmlFor={`${prefix}-reference`} className="version-comparison__field">
                    <span>Comparer avec</span>
                    <Select id={`${prefix}-reference`} value={referenceId || undefined} disabled={locked || multi}
                        showSearch allowClear optionFilterProp="label" loading={loading}
                        placeholder="Choisir une autre version"
                        notFoundContent="Aucune version correspondante"
                        onChange={value => comparison.selectReference(value || '')}
                        options={references.map(row => ({
                            value: row.document_version_id,
                            disabled: !row.eligible_for_comparison,
                            label: `${versionLabel(row)}${!row.eligible_for_comparison ? ` · ${comparisonIneligibilityLabel(row)}` : ''}`,
                        }))}
                    />
                </label>}
                {reference && <p className="version-comparison__direction" role="status">
                    Les modifications seront relevées dans <strong>{versionLabel(examined)}</strong> par rapport à <strong>{versionLabel(reference)}</strong>.
                </p>}
                <small className="version-comparison__limit">Vérifiez votre choix : une comparaison terminée ne peut pas être remplacée ici.</small>
                <ul className="version-comparison__versions" aria-label="Versions en préparation ou indisponibles">
                    {rows.filter(row => row.extraction_status !== 'succeeded' || (!row.eligible_for_comparison && row.document_version_id !== viewedVersionId)).map(row => {
                        const progress = tracking[row.document_version_id];
                        return <li key={row.document_version_id}>
                            <span>{versionLabel(row)}</span>
                            <small>{comparisonIneligibilityLabel(row)}</small>
                            {progress?.status === 'processing' && <progress aria-label={`Préparation de ${row.title}`} />}
                            {progress?.message && <small role={progress.status === 'poll_error' ? 'alert' : 'status'}>{progress.message}</small>}
                            {(row.extraction_status === 'failed' || progress?.status === 'poll_error') &&
                                <button type="button" disabled={locked || progress?.status === 'processing'}
                                    onClick={() => comparison.retry(row.document_version_id)}>Actualiser l’état de préparation</button>}
                        </li>;
                    })}
                </ul>
                {comparison.notice && <p role="status">{comparison.notice}</p>}
            </>}
        </>}
    </div>;
}
